/**
 * AI Model Picker
 * ===============
 * Settings tab where users select which platform LLM model is used for the
 * platform functions they trigger (worker building, intent parsing, etc.).
 *
 * - Reads `/api/me/llm-preference` to get the user's tier-allowed model list
 *   and current selection.
 * - PUTs back to the same endpoint when the user picks a model.
 * - Shows the credit-cost multiplier for each model so users see the trade-off
 *   before committing.
 * - Surfaces function-floor info ("some advanced functions always use ≥ X
 *   regardless of your selection") so Standard tier users know why their pick
 *   doesn't apply everywhere.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { CircleNotch, Lightning, CheckCircle, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';
import AISpendCard from './AISpendCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Tier badge colors
const TIER_BADGES = {
  light:    { bg: 'rgba(74,222,128,0.10)', fg: '#4ADE80', label: 'LIGHT' },
  standard: { bg: 'rgba(0,229,192,0.10)', fg: '#00E5C0', label: 'STANDARD' },
  advanced: { bg: 'rgba(168,85,247,0.10)', fg: '#A855F7', label: 'ADVANCED' },
  frontier: { bg: 'rgba(251,191,36,0.10)', fg: '#FBBF24', label: 'FRONTIER' },
};

const PROVIDER_LABELS = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
  grok: 'xAI',
};

export default function AIModelPicker() {
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState('');

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/me/llm-preference`, {}, orgId);
      if (res.ok) {
        const json = await res.json();
        setData(json);
        setSelected(json.selected_model);
      } else {
        toast.error('Could not load model preferences');
      }
    } catch {
      toast.error('Network error loading model preferences');
    }
    setLoading(false);
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!selected || !data || selected === data.selected_model) return;
    setSaving(true);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/me/llm-preference`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ model_id: selected }),
        },
        orgId,
      );
      if (res.ok) {
        toast.success('Model preference saved');
        await load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save preference');
      }
    } catch {
      toast.error('Network error saving preference');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10" data-testid="ai-model-picker-loading">
        <CircleNotch size={24} className="text-[#00E5C0] animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const dirty = selected !== data.selected_model;
  const advancedFunctions = Object.entries(data.function_floors || {})
    .filter(([_, tier]) => tier === 'advanced' || tier === 'frontier')
    .map(([fn]) => fn.replace(/_/g, ' '));

  return (
    <div className="space-y-6" data-testid="ai-model-picker">
      <div>
        <h2 className="text-lg font-semibold text-[#F0F4F8]">AI Model</h2>
        <p className="text-sm text-[#B0B8C4] mt-1">
          Pick the model that powers your workers and Command. More capable models cost more credits per call.
        </p>
      </div>

      <AISpendCard />

      <div className="space-y-2">
        {data.available_models.map((m) => {
          const isSelected = selected === m.model_id;
          const tierBadge = TIER_BADGES[m.tier] || TIER_BADGES.standard;
          return (
            <button
              key={m.model_id}
              onClick={() => setSelected(m.model_id)}
              data-testid={`model-option-${m.model_id}`}
              className={`w-full text-left p-4 rounded-xl border transition-all ${
                isSelected
                  ? 'border-[#00E5C0] bg-[rgba(0,229,192,0.04)]'
                  : 'border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] hover:border-[rgba(0,229,192,0.25)]'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  {isSelected ? (
                    <CheckCircle size={20} weight="fill" className="text-[#00E5C0]" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-[rgba(255,255,255,0.15)]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-[#F0F4F8]">{m.display_name}</span>
                    <span className="text-[10px] text-[#7D8896]">{PROVIDER_LABELS[m.provider] || m.provider}</span>
                    <span
                      className="text-[9px] font-bold tracking-[0.08em] px-1.5 py-0.5 rounded"
                      style={{ background: tierBadge.bg, color: tierBadge.fg }}
                    >
                      {tierBadge.label}
                    </span>
                    {m.is_default && (
                      <span className="text-[9px] text-[#7D8896] uppercase tracking-[0.08em]">Platform default</span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-[#B0B8C4]">
                    <Lightning size={12} className="text-[#FBBF24]" />
                    <span>
                      <strong className="text-[#F0F4F8]">{m.credit_multiplier}×</strong> credits per call
                    </span>
                    {m.credit_multiplier === 1.0 && <span className="text-[#7D8896]">(baseline)</span>}
                    {m.credit_multiplier < 1.0 && <span className="text-[#4ADE80]">(cheaper than baseline)</span>}
                    {m.credit_multiplier > 1.0 && <span className="text-[#7D8896]">vs baseline</span>}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {advancedFunctions.length > 0 && (
        <div className="flex items-start gap-3 p-4 bg-[rgba(168,85,247,0.04)] border border-[rgba(168,85,247,0.15)] rounded-xl" data-testid="floor-info">
          <Info size={18} className="text-[#A855F7] flex-shrink-0 mt-0.5" />
          <div className="text-xs text-[#B0B8C4]">
            <strong className="text-[#F0F4F8] block mb-1">Some advanced functions always use a stronger model.</strong>
            Functions like <em className="text-[#F0F4F8]">{advancedFunctions.slice(0, 4).join(', ')}</em>
            {advancedFunctions.length > 4 ? ` and ${advancedFunctions.length - 4} more` : ''} require deeper reasoning, so they automatically use the cheapest model with advanced capability — even when your selection is lighter. Credit cost reflects the model actually used.
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-2">
        <button
          onClick={() => setSelected(data.selected_model)}
          disabled={!dirty || saving}
          data-testid="ai-model-picker-cancel"
          className="px-4 py-2 text-sm text-[#B0B8C4] hover:text-[#F0F4F8] disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="ai-model-picker-save"
          className="px-5 py-2 bg-[#00E5C0] text-[#12161C] font-semibold text-sm rounded-lg hover:bg-[#00CCB0] disabled:opacity-50 transition-colors flex items-center gap-2"
        >
          {saving && <CircleNotch size={14} className="animate-spin" />}
          Save model preference
        </button>
      </div>
    </div>
  );
}
